import { useCallback, useEffect, useState } from "react"

import { useAuth } from "@/lib/auth"
import { isMissingDatabaseColumn } from "@/lib/supabase-relation-errors"
import { supabase } from "@/lib/supabase"
import type { ProfileRow } from "@/types/profile"
import { defaultNotificationPreferences } from "@/types/user-settings"
import type { NotificationPreferences } from "@/types/user-settings"

type ProfileUpdate = Partial<{
  full_name: string
  country_of_residence: string | null
  preferred_currency: string | null
  language: string
  theme: string
  date_format: string
  timezone: string | null
  avatar_url: string | null
  notification_preferences: NotificationPreferences
}>

function mergeProfile(r: Record<string, unknown> | null): ProfileRow | null {
  if (!r) return null
  const row = r as unknown as ProfileRow
  const pref = r.preferred_currency
  const tzRaw = r.timezone
  const timezone =
    typeof tzRaw === "string" && tzRaw.trim().length > 0 ? tzRaw.trim() : null
  return {
    ...row,
    timezone,
    preferred_currency: typeof pref === "string" ? pref : null,
    notification_preferences:
      (r.notification_preferences as NotificationPreferences) ?? defaultNotificationPreferences(),
    workspace_count: typeof r.workspace_count === "number" ? r.workspace_count : 0,
    extraction_count: typeof r.extraction_count === "number" ? r.extraction_count : 0,
  }
}

function omitTimezone<T extends ProfileUpdate>(patch: T): Omit<T, "timezone"> {
  const { timezone: _tz, ...rest } = patch
  return rest
}

export function useUserProfile() {
  const { user } = useAuth()
  const [profile, setProfile] = useState<ProfileRow | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    if (!user?.id) {
      setProfile(null)
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const { data, error: q } = await supabase.from("profiles").select("*").eq("id", user.id).maybeSingle()
      if (q) throw q
      setProfile(mergeProfile(data as Record<string, unknown> | null))
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load profile")
      setProfile(null)
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const updateProfile = useCallback(
    async (patch: ProfileUpdate) => {
      if (!user?.id) return { error: new Error("Not signed in") }

      const runUpdate = async (body: ProfileUpdate) =>
        supabase.from("profiles").update(body).eq("id", user.id).select("*").maybeSingle()

      let { data, error: up } = await runUpdate(patch)

      // Optional `profiles.timezone` — strip and retry remaining fields when column is absent.
      if (up && patch.timezone !== undefined && isMissingDatabaseColumn(up, ["timezone"])) {
        const rest = omitTimezone(patch)
        if (Object.keys(rest).length === 0) {
          return {
            error: Object.assign(new Error("Timezone preference is not available yet."), {
              code: "TIMEZONE_COLUMN_MISSING",
            }),
          }
        }
        ;({ data, error: up } = await runUpdate(rest))
      }

      if (up) return { error: up }
      setProfile(mergeProfile(data as Record<string, unknown> | null))
      return { error: null }
    },
    [user]
  )

  return { profile, loading, error, refresh, updateProfile }
}
